import React from 'react';
import { X, LayoutDashboard, Users, LogOut, Settings, Plus, ChevronLeft, ChevronRight, FileText, Mail, Building2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthentipetLogo } from '../AuthentipetLogo';
import { LucideIcon } from 'lucide-react';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
    isCollapsed: boolean;
    toggleCollapse: () => void;
    onLogout: () => void;
    ownerName: string;
    isAdmin?: boolean;
    onCreateCompany?: () => void;
    onCreateComplex?: () => void;
}

interface NavItemProps {
    icon: LucideIcon | React.ComponentType<{ className?: string; size?: number }>;
    label: string;
    path?: string;
    onClick: () => void;
    disabled?: boolean;
    isActive: boolean;
    isCollapsed: boolean;
    theme: 'light' | 'dark';
}

interface ActionItemProps {
    icon: LucideIcon;
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
    isCollapsed: boolean;
    theme: 'light' | 'dark';
}

const NavItem: React.FC<NavItemProps & { isAdmin?: boolean }> = ({ icon: Icon, label, onClick, disabled = false, isActive, isCollapsed, theme, isAdmin }) => {
    // Determine accent color class based on admin status
    // Default (non-admin): storgia-blurple
    // Admin: storgia-green

    const activeBgDark = isAdmin ? 'bg-storgia-green/20' : 'bg-storgia-blurple/20';
    const activeTextLight = isAdmin ? 'text-storgia-green' : 'text-storgia-blurple';

    const inactiveTextDark = isAdmin ? 'text-storgia-green/70 hover:text-white' : 'text-storgia-light-blurple hover:text-white';
    const inactiveTextLight = isAdmin ? 'text-gray-700 hover:text-storgia-green' : 'text-gray-700 hover:text-storgia-blurple';
    const inactiveBgDarkHover = isAdmin ? 'hover:bg-storgia-green/10' : 'hover:bg-storgia-blurple/10';

    const indicatorColor = isAdmin
        ? (theme === 'dark' ? 'bg-storgia-green' : 'bg-emerald-600')
        : (theme === 'dark' ? 'bg-storgia-blurple' : 'bg-indigo-600');

    // Focus ring colors based on admin status and theme
    const focusRingColor = isAdmin
        ? 'focus-visible:ring-storgia-green'
        : 'focus-visible:ring-storgia-blurple';

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            type="button"
            aria-label={label}
            aria-current={isActive ? 'page' : undefined}
            title={isCollapsed ? label : undefined}
            className={`
          w-full flex items-center gap-3 px-4 py-3 font-league-spartan rounded-xl transition-all duration-200 font-medium group relative
          focus-visible:outline-none focus-visible:ring-2 ${focusRingColor} focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
          ${isActive
                    ? theme === 'dark' ? `${activeBgDark} text-white` : `bg-gray-100 ${activeTextLight}`
                    : theme === 'dark' ? `${inactiveTextDark} ${inactiveBgDarkHover}` : `${inactiveTextLight} hover:bg-gray-100`
                }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${isCollapsed ? 'justify-center px-2' : ''}
        `}
        >
            <Icon className={`h-5 w-5 flex-shrink-0 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} aria-hidden="true" />
            {!isCollapsed && <span>{label}</span>}
            {isCollapsed && isActive && (
                <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full ${indicatorColor}`} aria-hidden="true" />
            )}
        </button>
    );
};

const ActionItem: React.FC<ActionItemProps> = ({ icon: Icon, label, onClick, variant = 'primary', isCollapsed, theme }) => (
    <button
        onClick={onClick}
        type="button"
        aria-label={label}
        title={isCollapsed ? label : undefined}
        className={`
        w-full flex items-center gap-3 px-4 py-3 font-league-spartan rounded-xl transition-all duration-200 font-medium mb-2 group
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-storgia-green focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
        ${variant === 'primary'
                ? theme === 'dark' ? 'bg-storgia-green text-white hover:bg-storgia-green/90 shadow-lg shadow-storgia-green/20' : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-200'
                : theme === 'dark' ? 'bg-storgia-green/20 text-storgia-green hover:bg-storgia-green/30' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
            }
        ${isCollapsed ? 'justify-center px-2' : ''}
      `}
    >
        <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        {!isCollapsed && <span>{label}</span>}
    </button>
);

export const Sidebar: React.FC<SidebarProps> = ({
    isOpen,
    onClose,
    isCollapsed,
    toggleCollapse,
    onLogout,
    ownerName,
    isAdmin = false,
    onCreateCompany,
    onCreateComplex
}) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavigate = (path: string) => {
        navigate(path);
        onClose();
    };

    const isActive = (path: string) => location.pathname === path;

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-storgia-black/70 z-40 transition-opacity backdrop-blur-sm lg:hidden"
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            {/* Drawer */}
            <div className={`
        fixed inset-y-0 right-0 lg:right-auto lg:left-0 z-50 shadow-2xl transform transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'lg:w-20' : 'lg:w-72'}
        ${theme === 'dark' ? 'bg-storgia-dark border-r border-storgia-light-blurple/20' : 'bg-white border-r border-gray-200'}
      `}>

                {/* Header */}
                <div className={`
          p-6 border-b flex items-center relative transition-all duration-300
          ${isCollapsed ? 'justify-center px-2' : 'justify-between'}
          bg-gradient-to-r from-storgia-blurple to-storgia-dark-blurple
          ${theme === 'dark' ? 'border-storgia-light-blurple/20' : 'border-gray-200'}
        `}>
                    <div className={`flex items-center gap-2 overflow-hidden transition-all duration-300 ${isCollapsed ? 'w-8' : 'w-full'}`}>
                        <AuthentipetLogo size={32} />
                        {!isCollapsed && (
                            <div className="min-w-0">
                                <h2 className="font-bold font-league-spartan text-lg text-white uppercase tracking-[-1px] truncate">Authentipet</h2>
                                <p className="text-xs text-storgia-light-blurple/90 truncate">Hi, {ownerName && ownerName.length > 0 ? ownerName.split(' ')[0] : 'User'}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        type="button"
                        aria-label="Close navigation menu"
                        className="p-2 hover:bg-storgia-light-blurple/20 rounded-full transition-colors lg:hidden absolute right-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                    >
                        <X className="h-5 w-5 text-white" aria-hidden="true" />
                    </button>
                </div>

                {/* Toggle Button (Desktop Only) */}
                <button
                    onClick={toggleCollapse}
                    type="button"
                    aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    aria-expanded={!isCollapsed}
                    className={`
            hidden lg:flex absolute -right-3 top-20 h-6 w-6 items-center justify-center rounded-full border shadow-md z-50 transition-colors
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-storgia-blurple focus-visible:ring-offset-2
            ${theme === 'dark'
                            ? 'bg-storgia-dark border-storgia-light-blurple/20 text-storgia-light-blurple hover:text-white hover:bg-storgia-blurple'
                            : 'bg-white border-gray-200 text-gray-500 hover:text-indigo-600 hover:border-indigo-200'
                        }
          `}
                >
                    {isCollapsed ? <ChevronRight className="h-3 w-3" aria-hidden="true" /> : <ChevronLeft className="h-3 w-3" aria-hidden="true" />}
                </button>

                <nav
                    role="navigation"
                    aria-label="Main navigation"
                    className="p-4 space-y-2 flex-1 overflow-y-auto overflow-x-hidden"
                >
                    {isAdmin && (
                        <>
                            <NavItem
                                icon={AuthentipetLogo}
                                label="Admin Overview"
                                path="/admin"
                                onClick={() => handleNavigate('/admin')}
                                isActive={isActive('/admin')}
                                isCollapsed={isCollapsed}
                                theme={theme}
                                isAdmin={isAdmin}
                            />
                            <NavItem
                                icon={Mail}
                                label="Email Analytics"
                                path="/analytics/emails"
                                onClick={() => handleNavigate('/analytics/emails')}
                                isActive={isActive('/analytics/emails')}
                                isCollapsed={isCollapsed}
                                theme={theme}
                                isAdmin={isAdmin}
                            />
                        </>
                    )}

                    {isAdmin && onCreateCompany && (
                        <ActionItem
                            icon={Plus}
                            label="New Company"
                            onClick={() => { onCreateCompany(); onClose(); }}
                            isCollapsed={isCollapsed}
                            theme={theme}
                        />
                    )}

                    {!isAdmin && onCreateComplex && (
                        <ActionItem
                            icon={Plus}
                            label="Add Property"
                            onClick={() => { onCreateComplex(); onClose(); }}
                            isCollapsed={isCollapsed}
                            theme={theme}
                        />
                    )}

                    <div className={`h-px my-4 ${theme === 'dark' ? 'bg-storgia-light-blurple/10' : 'bg-gray-100'}`} />

                    <NavItem
                        icon={LayoutDashboard}
                        label="Dashboard"
                        path="/dashboard"
                        onClick={() => handleNavigate('/dashboard')}
                        isActive={isActive('/dashboard')}
                        isCollapsed={isCollapsed}
                        theme={theme}
                        isAdmin={isAdmin}
                    />

                    <NavItem
                        icon={Building2}
                        label="Properties"
                        path="/properties"
                        onClick={() => handleNavigate('/properties')}
                        isActive={location.pathname === '/properties' || location.pathname.startsWith('/complex')}
                        isCollapsed={isCollapsed}
                        theme={theme}
                        isAdmin={isAdmin}
                    />

                    <NavItem
                        icon={Users}
                        label="Team Management"
                        path="/users"
                        onClick={() => handleNavigate('/users')}
                        isActive={isActive('/users')}
                        isCollapsed={isCollapsed}
                        theme={theme}
                        isAdmin={isAdmin}
                    />

                    <NavItem
                        icon={FileText}
                        label="Documents"
                        path="/documents"
                        onClick={() => handleNavigate('/documents')}
                        isActive={isActive('/documents')}
                        isCollapsed={isCollapsed}
                        theme={theme}
                        isAdmin={isAdmin}
                    />

                    <NavItem
                        icon={Settings}
                        label="Settings"
                        path="/profile"
                        onClick={() => handleNavigate('/profile')}
                        isActive={isActive('/profile')}
                        isCollapsed={isCollapsed}
                        theme={theme}
                        isAdmin={isAdmin}
                    />
                </nav>

                <div className={`absolute bottom-0 left-0 right-0 p-4 border-t ${theme === 'dark' ? 'border-storgia-light-blurple/20 bg-storgia-black/50' : 'border-gray-200 bg-gray-50'}`}>
                    <button
                        onClick={onLogout}
                        type="button"
                        aria-label="Sign Out"
                        title={isCollapsed ? "Sign Out" : undefined}
                        className={`
              w-full flex items-center gap-3 px-4 py-3 font-league-spartan rounded-xl transition-colors font-medium
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-storgia-pink focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
              ${theme === 'dark' ? 'text-storgia-pink hover:bg-storgia-pink/10' : 'text-red-600 hover:bg-red-50'}
              ${isCollapsed ? 'justify-center px-2' : ''}
            `}
                    >
                        <LogOut className="h-5 w-5" aria-hidden="true" />
                        {!isCollapsed && <span>Sign Out</span>}
                    </button>
                </div>

            </div>
        </>
    );
};
